import React from 'react';
import {
  canRegenerate,
  entryOperationLocked,
  GENRE_DRAFTS,
  MAX_REGEN,
} from './entry_logic';

// 見出し付きの定型たたき台（ジャンル選択時に挿入。具体は捏造しない）
const BODY_SKELETON =
  '【こんな方におすすめ】\n・はじめての方も歓迎です\n\n【当日の流れ】\n・（自由に編集してください）\n\n【持ち物・参加費】\n・手ぶらでOK';

// 本番の実人気ジャンル（Category.pageview 上位）
const GENRES = Object.keys(GENRE_DRAFTS);

const C = {
  bg: '#F6F6F6',
  surface: '#FFFFFF',
  ink: '#333333',
  inkSoft: '#666666',
  muted: '#8B9097',
  line: '#ECECEE',
  primary: '#FF6467',
  wash: '#FFEAEA',
  blue: '#48A3E9',
  done: '#8CB838',
  danger: '#E05C35',
};

const CARD_SHADOW =
  '0 1px 2px rgba(30,30,40,.03), 0 10px 26px -16px rgba(30,30,40,.14)';

const STYLES = `
.tg-entry{background:${C.bg};min-height:100vh;padding:16px 18px 132px;}
.tg-entry-col{max-width:480px;margin:0 auto;}
.tg-entry-genres{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.tg-entry-ai-field{background:${C.surface};border:1px solid ${C.line};border-radius:12px;padding:12px 14px;}
.tg-entry-genre{border-color:${C.line}!important;color:${C.inkSoft}!important;}
.tg-entry-genre[aria-pressed="true"]{border-color:${C.primary}!important;background:${C.wash}!important;color:${C.primary}!important;}
.tg-entry button:focus,.tg-entry input:focus{outline:none;}
.tg-entry button:focus-visible{outline:3px solid rgba(255,100,103,.25);outline-offset:2px;}
.tg-entry-title-card:focus-within,.tg-entry-ai-field:focus-within{border-color:rgba(255,100,103,.55)!important;}
.tg-entry-cta{position:fixed;left:0;right:0;bottom:0;background:${C.surface};border-top:1px solid ${C.line};padding:12px 16px calc(12px + env(safe-area-inset-bottom));z-index:100;}
.tg-entry-cta-inner{max-width:480px;margin:0 auto;}
.tg-entry-input::placeholder{color:${C.muted};opacity:1;font-weight:400;}
.tg-entry-input:-webkit-autofill,.tg-entry-input:-webkit-autofill:hover,.tg-entry-input:-webkit-autofill:focus{-webkit-text-fill-color:${C.ink};transition:background-color 600000s 0s,color 600000s 0s;}
.tg-entry-skel{display:block;height:14px;border-radius:5px;background:linear-gradient(90deg,${C.line} 25%,#f5f5f5 37%,${C.line} 63%);background-size:400% 100%;animation:tg-entry-shimmer 1.4s ease infinite;}
@keyframes tg-entry-shimmer{0%{background-position:100% 0}100%{background-position:0 0}}
@media (prefers-reduced-motion:reduce){.tg-entry-skel{animation:none;}}
@media (min-width:860px){
  .tg-entry{padding:28px 24px 140px;}
}
`;

const S = {
  card: {
    background: C.surface,
    border: `1px solid ${C.line}`,
    borderRadius: 16,
    padding: '16px 18px',
    boxShadow: CARD_SHADOW,
    marginBottom: 12,
  },
  // @context TGW-2195 入口の主役はイベント名の入力。AI候補やジャンルより一段大きく、押しやすい面にする。
  titleCard: {
    background: C.surface,
    border: `1px solid ${C.line}`,
    borderRadius: 20,
    padding: '20px 22px 24px',
    boxShadow: CARD_SHADOW,
    marginBottom: 14,
  },
  titleInput: {
    fontSize: 20,
    lineHeight: 1.5,
    padding: '6px 0 2px',
  },
  rowBetween: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  lab: {
    fontSize: 12,
    fontWeight: 800,
    color: C.inkSoft,
    letterSpacing: '.02em',
  },
  count: { fontSize: 11, color: C.muted, fontVariantNumeric: 'tabular-nums' },
  input: {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: 0,
    fontSize: 16,
    fontWeight: 700,
    color: C.ink,
  },
  helper: { fontSize: 12, color: C.inkSoft, margin: '8px 2px 14px' },
  warn: { fontSize: 12, color: C.danger, margin: '0 2px 14px' },
  headline: {
    fontSize: 24,
    fontWeight: 800,
    color: C.ink,
    lineHeight: 1.35,
    letterSpacing: '-.01em',
    margin: '14px 0 8px',
  },
  sub: { fontSize: 13, color: C.inkSoft, margin: '0 0 24px' },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '36px 2px 20px',
    color: C.muted,
    fontSize: 12,
    fontWeight: 700,
  },
  dividerLine: { flex: 1, height: 1, background: C.line },
  secLabel: { fontSize: 13, fontWeight: 800, color: C.ink, margin: '0 0 12px' },
  tile: {
    background: C.surface,
    border: `1px solid ${C.line}`,
    borderRadius: 16,
    padding: '14px 12px',
    fontSize: 13,
    fontWeight: 700,
    color: C.ink,
    cursor: 'pointer',
    textAlign: 'center',
    boxShadow: CARD_SHADOW,
  },
  tileNote: { fontSize: 12, color: C.inkSoft, margin: '8px 2px 0' },
  aiAssist: {
    background: '#FFF8F8',
    border: `1px solid ${C.wash}`,
    borderRadius: 16,
    padding: '18px 18px 20px',
    marginTop: 32,
  },
  aiAssistHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    color: C.primary,
    fontSize: 13,
    fontWeight: 800,
  },
  aiAssistDescription: {
    color: C.inkSoft,
    fontSize: 12,
    lineHeight: 1.6,
    margin: '6px 0 14px',
  },
  rowItem: { display: 'flex', alignItems: 'center', gap: 12 },
  rowMain: { flex: 1, minWidth: 0 },
  val: { fontSize: 15, fontWeight: 700, color: C.ink, wordBreak: 'break-word' },
  hint: { fontSize: 12, color: C.muted, marginTop: 3 },
  chev: { flex: 'none', fontSize: 16, color: C.muted, lineHeight: 1 },
  aiPill: {
    fontSize: 10,
    fontWeight: 800,
    color: C.primary,
    background: C.wash,
    borderRadius: 5,
    padding: '1px 6px',
    flex: 'none',
  },
  sugRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '9px 0',
  },
  sugText: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.5,
    color: C.inkSoft,
  },
  // @context TGW-2195 AI候補がイベント名欄より目立つと「選ぶ画面」に見えるため、影を外し文字も一段控えめにする。
  sugCard: {
    background: 'rgba(255,255,255,.6)',
    border: `1px solid ${C.line}`,
    borderRadius: 16,
    padding: '8px 16px 12px',
    marginBottom: 12,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 700,
    color: C.primary,
  },
};

const cardButton = (disabled) => ({
  ...S.card,
  ...S.rowItem,
  display: 'flex',
  width: '100%',
  textAlign: 'left',
  cursor: disabled ? 'default' : 'pointer',
});

const nextButton = (enabled) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  padding: 14,
  borderRadius: 40,
  fontSize: 15,
  fontWeight: 800,
  border: 'none',
  cursor: enabled ? 'pointer' : 'default',
  color: enabled ? '#fff' : C.muted,
  background: enabled ? C.primary : C.line,
  boxShadow: enabled ? '0 10px 24px -10px rgba(255,100,103,.7)' : 'none',
});

// @context TGW-2195 入口（S0）。デザイン第4版のマークアップを JSX に写したもの。
//   上部に独自ヘッダーは描かない（RN はネイティブ帯を、Web は共通ヘッダーを描くため）。
const EntryScreen = ({
  title,
  onTitleChange,
  onBack,
  suggest,
  onPickSuggestion,
  onRegenerate,
  genre,
  onGenre,
  aiTheme,
  setAiTheme,
  onAI,
  onNext,
  onPastCopy,
  showPastCopy,
  saving,
  aiGenerating,
  error,
  aiError,
  autoFocusTitle,
  onFocusScrollForAndroid,
}) => {
  const titleLen = (title || '').length;
  // @context TGW-2195 AI生成中の別操作で、遅れて返った生成結果が選択内容や遷移後の state を上書きしないよう入口をロックする。
  const operationLocked = entryOperationLocked({ saving, aiGenerating });
  const canNext = (title || '').trim().length > 0 && !operationLocked;
  const aiDisabled = !(aiTheme || '').trim() || !!aiGenerating || !!saving;
  const status = suggest ? suggest.status : 'idle';
  const regenLeft = suggest ? MAX_REGEN - suggest.regenCount : 0;

  return (
    <div className="tg-entry">
      <style>{STYLES}</style>

      <div className="tg-entry-col">
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 14,
            color: C.inkSoft,
            fontWeight: 600,
            padding: '6px 8px',
            marginLeft: -8,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <span style={{ fontSize: 21, lineHeight: 1 }}>‹</span>戻る
        </button>

        <h1 style={S.headline}>
          どんな会を、
          <br />
          ひらきますか？
        </h1>
        <p style={S.sub}>
          イベント名だけで始められます。あとから変更できます
        </p>

        {/* タイトル入力 */}
        {/* @context TGW-2195 入力欄の青い枠は消したが、キーボード操作で現在位置が見えなくなるため、囲みの枠線だけ淡い赤にする */}
        <div className="tg-entry-title-card" style={S.titleCard}>
          <div style={S.rowBetween}>
            <span style={{ ...S.lab, fontSize: 13 }}>イベント名</span>
            <span style={S.count}>
              <b style={{ color: C.inkSoft, fontWeight: 800 }}>{titleLen}</b> /
              60
            </span>
          </div>
          {/* @context TGW-2195 入口の初回表示でイベント名をすぐ入力できるようにし、再描画ではフォーカスを奪わない。 */}
          <input
            type="text"
            className="tg-entry-input"
            aria-label="イベント名"
            autoFocus={autoFocusTitle}
            value={title || ''}
            maxLength={60}
            onChange={(e) => onTitleChange(e.target.value)}
            onFocus={onFocusScrollForAndroid}
            placeholder="例：平日夜の、ゆるっとカレー交流会"
            style={{ ...S.input, ...S.titleInput }}
            disabled={operationLocked}
          />
        </div>
        {!!error && <p style={S.warn}>{error}</p>}

        {/* AI候補（F1-5, F1-7, F1-13） */}
        {status === 'loading' && (
          <div style={S.sugCard}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  padding: '9px 0',
                  borderTop: i === 0 ? 'none' : `1px solid ${C.line}`,
                }}
              >
                <i className="tg-entry-skel" />
              </div>
            ))}
          </div>
        )}
        {status === 'ready' && (
          <div style={S.sugCard}>
            {suggest.titles.map((t, i) => (
              <button
                key={t}
                type="button"
                onClick={() => onPickSuggestion(t)}
                disabled={operationLocked}
                style={{
                  ...S.sugRow,
                  paddingTop: i === 0 ? 6 : 9,
                  borderTop: i === 0 ? 'none' : `1px solid ${C.line}`,
                }}
              >
                <span style={S.aiPill}>AI</span>
                <span style={S.sugText}>{t}</span>
                <span style={S.chev}>›</span>
              </button>
            ))}
            {canRegenerate(suggest) && (
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  onClick={onRegenerate}
                  disabled={operationLocked}
                  style={S.linkBtn}
                >
                  ほかの候補を見る（あと {regenLeft} 回）
                </button>
              </div>
            )}
          </div>
        )}

        {/* 区切り */}
        <div style={S.divider}>
          <span style={S.dividerLine} />
          イベント名が思いつかないとき
          <span style={S.dividerLine} />
        </div>

        {/* 人気ジャンル */}
        <p style={{ ...S.secLabel, margin: '0 0 4px' }}>ジャンルから選ぶ</p>
        {/* @context TGW-2195 見出しだけでは「ジャンルの分類を設定する」と読まれ、押すとイベント名と紹介文が入ることが伝わらないため、結果を先に書く。 */}
        <p style={{ fontSize: 12, color: C.inkSoft, margin: '0 0 12px' }}>
          選ぶと、イベント名と紹介文の例文が入ります（あとで直せます）
        </p>
        <div className="tg-entry-genres">
          {GENRES.map((label) => (
            <button
              key={label}
              type="button"
              className="tg-entry-genre"
              aria-pressed={genre === label}
              onClick={() => onGenre(label, BODY_SKELETON)}
              disabled={operationLocked}
              style={S.tile}
            >
              {label}
            </button>
          ))}
        </div>
        {genre && (
          <p style={S.tileNote}>
            「{genre}」をもとにイベント名と紹介文を入力しました
          </p>
        )}

        {/* @context TGW-2195 AI補助は履歴コピーと同列の遷移カードにせず、入力中にその場で使える常設の補助機能として示す。 */}
        <section aria-labelledby="tg-entry-ai-heading" style={S.aiAssist}>
          <div id="tg-entry-ai-heading" style={S.aiAssistHead}>
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2.75c.45 3.77 2.48 5.8 6.25 6.25-3.77.45-5.8 2.48-6.25 6.25C11.55 11.48 9.52 9.45 5.75 9 9.52 8.55 11.55 6.52 12 2.75Z"
                fill="currentColor"
              />
              <path
                d="M18.5 14.5c.22 1.82 1.18 2.78 3 3-1.82.22-2.78 1.18-3 3-.22-1.82-1.18-2.78-3-3 1.82-.22 2.78-1.18 3-3Z"
                fill="currentColor"
              />
            </svg>
            <span>AIにおまかせ</span>
          </div>
          <p style={S.aiAssistDescription}>
            一言から、イベント名と紹介文を作ります
          </p>
          <div className="tg-entry-ai-field">
            <input
              id="tg-entry-ai-theme"
              type="text"
              autoComplete="off"
              aria-label="イベントの特徴を一言"
              className="tg-entry-input"
              value={aiTheme || ''}
              maxLength={100}
              onChange={(e) => setAiTheme(e.target.value)}
              placeholder="例：カレー好きが気軽に集まる会"
              style={S.input}
              disabled={operationLocked}
            />
          </div>
          {!!aiError && <p style={{ ...S.warn, marginTop: 10 }}>{aiError}</p>}
          <button
            type="button"
            disabled={aiDisabled}
            onClick={() => onAI(aiTheme)}
            style={{ ...nextButton(!aiDisabled), marginTop: 14 }}
          >
            {aiGenerating ? '作成しています…' : 'AIで作る'}
          </button>
        </section>

        {/* 過去のイベントをコピー（F1-11） */}
        {showPastCopy && (
          <section style={{ marginTop: 24 }}>
            <button
              type="button"
              onClick={onPastCopy}
              disabled={operationLocked}
              style={cardButton(operationLocked)}
            >
              <span style={S.rowMain}>
                <span style={{ ...S.val, display: 'block' }}>
                  過去のイベントをコピー
                </span>
                <span style={{ ...S.hint, display: 'block' }}>
                  日時だけ選び直せば使えます
                </span>
              </span>
              <span style={S.chev}>›</span>
            </button>
          </section>
        )}
      </div>

      {/* 下部固定 CTA */}
      <div className="tg-entry-cta">
        <div className="tg-entry-cta-inner">
          <button
            type="button"
            disabled={!canNext}
            onClick={() => onNext(title)}
            style={nextButton(canNext)}
          >
            {saving ? '保存しています…' : '次へ ›'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntryScreen;
